/**
 * O que faz: carrega a prévia visual dos anúncios CTWA de uma conversa.
 * Onde usa: ChatWindow, para colar o cartão do anúncio na mensagem correspondente.
 * Conexões: RPC get_chat_lead_ad_previews e React Query.
 * Tabelas: leitura indireta de lead_ad_attributions (sem ctwa_clid).
 * Data: 11/09/2026
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LeadAdPreview {
  whatsapp_message_id: string;
  titulo_anuncio: string | null;
  copy_anuncio: string | null;
  url_anuncio: string | null;
  url_midia: string | null;
  url_miniatura: string | null;
  miniatura_bucket: string | null;
  miniatura_object_key: string | null;
  url_miniatura_assinada: string | null;
  plataforma: string | null;
  entrada_em: string | null;
}

const textoOpcional = (valor: unknown): string | null => {
  if (typeof valor !== 'string') return null;
  const texto = valor.trim();
  return texto.length > 0 ? texto : null;
};

export const leadAdPreviewKeys = {
  conversation: (clinicaId: string, leadId: string) =>
    ['lead-ad-previews', clinicaId, leadId] as const,
};

const TAMANHO_LOTE_RENOVACAO = 50;

export const renovarUrlsMiniaturas = async (
  previews: LeadAdPreview[],
): Promise<Map<string, string>> => {
  const renovaveis = previews.filter(
    (preview) => preview.miniatura_bucket && preview.miniatura_object_key,
  );
  const urlsAssinadas = new Map<string, string>();

  for (let inicio = 0; inicio < renovaveis.length; inicio += TAMANHO_LOTE_RENOVACAO) {
    const lote = renovaveis.slice(inicio, inicio + TAMANHO_LOTE_RENOVACAO);

    try {
      // A função limita cada chamada a 50 itens; dividir aqui evita miniaturas ignoradas no histórico longo.
      const { data, error } = await supabase.functions.invoke('renew-minio-urls', {
        body: {
          attachments: lote.map((preview) => ({
            id: preview.whatsapp_message_id,
            objectKey: preview.miniatura_object_key as string,
            bucket: preview.miniatura_bucket as string,
          })),
        },
      });

      // A URL externa continua disponível como fallback; renovar imagem não pode derrubar o cartão inteiro.
      if (error || !data?.urls || typeof data.urls !== 'object') continue;

      for (const [idMensagem, url] of Object.entries(data.urls as Record<string, unknown>)) {
        if (typeof url === 'string' && url.trim()) urlsAssinadas.set(idMensagem, url.trim());
      }
    } catch {
      // Falha de rede também é tolerada para que a URL legada ainda possa ser tentada pelo cartão.
      continue;
    }
  }

  return urlsAssinadas;
};

export const useLeadAdPreviews = (
  clinicaId: string | null | undefined,
  leadId: string | null | undefined,
  sinalAtualizacao?: string | null,
) => {
  const queryClient = useQueryClient();
  const habilitado = Boolean(clinicaId && leadId);

  const consulta = useQuery({
    queryKey: habilitado
      ? leadAdPreviewKeys.conversation(clinicaId!, leadId!)
      : ['lead-ad-previews', 'desabilitado'],
    enabled: habilitado,
    staleTime: 30_000,
    queryFn: async (): Promise<Map<string, LeadAdPreview>> => {
      // RPC get_chat_lead_ad_previews: SELECT do criativo visível da conversa.
      // Campos: título, copy, URLs de anúncio/mídia/miniatura, referências privadas do MinIO e plataforma.
      // Filtros: clínica e lead da conversa aberta; a RPC confere permissão.
      // Risco: click ID não entra neste retorno de propósito; bucket/chave não são URLs públicas.
      const { data, error } = await supabase.rpc('get_chat_lead_ad_previews' as never, {
        p_clinica_id: clinicaId,
        p_lead_id: leadId,
      } as never);

      if (error) {
        throw new Error(`Não foi possível carregar os anúncios desta conversa: ${error.message}`);
      }

      const previews = ((data ?? []) as Record<string, unknown>[])
        .map((linha): LeadAdPreview | null => {
          const idMensagem = textoOpcional(linha.whatsapp_message_id);
          if (!idMensagem) return null;

          return {
            whatsapp_message_id: idMensagem,
            titulo_anuncio: textoOpcional(linha.titulo_anuncio),
            copy_anuncio: textoOpcional(linha.copy_anuncio),
            url_anuncio: textoOpcional(linha.url_anuncio),
            url_midia: textoOpcional(linha.url_midia),
            url_miniatura: textoOpcional(linha.url_miniatura),
            miniatura_bucket: textoOpcional(linha.miniatura_bucket),
            miniatura_object_key: textoOpcional(linha.miniatura_object_key),
            url_miniatura_assinada: null,
            plataforma: textoOpcional(linha.plataforma),
            entrada_em: textoOpcional(linha.entrada_em),
          };
        })
      .filter((preview): preview is LeadAdPreview => preview !== null);

      const urlsAssinadas = await renovarUrlsMiniaturas(previews);
      return new Map(previews.map((preview) => [
        preview.whatsapp_message_id,
        {
          ...preview,
          url_miniatura_assinada: urlsAssinadas.get(preview.whatsapp_message_id) ?? null,
        },
      ]));
    },
  });

  useEffect(() => {
    if (!habilitado || !sinalAtualizacao) return;

    // O n8n grava a atribuição depois da mensagem; espera um pouco e busca de novo.
    const temporizador = window.setTimeout(() => {
      void queryClient.invalidateQueries({
        queryKey: leadAdPreviewKeys.conversation(clinicaId!, leadId!),
      });
    }, 2500);

    return () => window.clearTimeout(temporizador);
  }, [clinicaId, habilitado, leadId, queryClient, sinalAtualizacao]);

  return consulta;
};
